<template>
    <form id="mainForm" action="#" method="post">
        <Card>
            <template #title>
                <Toolbar>
                    <template #left>
                        <div class="p-card-title">
                            [{{ mode }}] {{ $t('criteria') }}
                        </div>
                        <div style="margin-left: 10px" v-if="mode != this.$FORM_MODE_CREATE">
                            <Button type="button" icon="pi pi-info" @click="toggle" aria:haspopup="true"
                                aria-controls="overlay_panel" class="p-button-rounded p-button-outlined p-button-sm" />

                            <OverlayPanel ref="op" appendTo="body" :showCloseIcon="true" id="overlay_panel"
                                style="width: 550px" :breakpoints="{ '960px': '80vw' }">
                                <div class="grid field">
                                    <div class="row">
                                        <i class="pi pi-plus-circle" /> {{ this.$formatDateTime(header.Created_Date) }}
                                    </div>
                                    <div class="row">
                                        &nbsp;by {{ header.Created_By }}
                                    </div>
                                </div>
                                <div class="grid field" v-if="header.Edited_Date">
                                    <div class="row">
                                        <i class="pi pi-pencil" /> {{ this.$formatDateTime(header.Edited_Date) }}
                                    </div>
                                    <div class="row">
                                        &nbsp;by {{ header.Edited_By }}
                                    </div>
                                </div>
                                <div class="grid field" v-if="header.Deleted_Date">
                                    <div class="row">
                                        <i class="pi pi-trash" /> {{ this.$formatDateTime(header.Deleted_Date) }}
                                    </div>
                                    <div class="row">
                                        &nbsp;by {{ header.Deleted_By }}
                                    </div>
                                </div>
                            </OverlayPanel>
                        </div>
                    </template>

                    <template #right>
                        <Button icon="pi pi-chevron-left" label="Back" class="p-button mr-2" @click="backToIndex" />
                        <Button icon="pi pi-save" label="Save" class="p-button-success" @click="confirm($event)"
                            v-if="mode != this.$FORM_MODE_DELETE && mode != this.$FORM_MODE_VIEW" />
                        <Button icon="pi pi-trash" label="Delete" class="p-button-danger" @click="confirm($event)"
                            v-if="mode == this.$FORM_MODE_DELETE" />
                        <ConfirmPopup></ConfirmPopup>
                    </template>
                </Toolbar>
            </template>
            <template #content>
                <div class="p-inputtext-sm p-fluid">
                    <div class="grid">
                        <div class="col-6">
                            <div class="field grid">
                                <label for="Code" class="col-4">{{ $t('year') }}<span style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <Dropdown v-model="header.Period_Year" v-if="header" :options="years"
                                        optionLabel="value" optionValue="value" :placeholder="$t('year')"
                                        :disabled="mode != this.$FORM_MODE_CREATE"
                                        :readonly="mode != this.$FORM_MODE_CREATE"
                                        :class="{ 'p-invalid': submitted && !header.Period_Year }" />
                                    <Skeleton v-else-if="!header" />
                                    <small
                                        v-if="header && !header.Period_Year && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('year')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Grade" class="col-4">{{ $t('rate') }}<span style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <Dropdown v-model="header.Grade" v-if="header" :options="rates" optionLabel="value"
                                        optionValue="value" :placeholder="$t('rate')"
                                        :disabled="mode != this.$FORM_MODE_CREATE"
                                        :readonly="mode != this.$FORM_MODE_CREATE"
                                        :class="{ 'p-invalid': submitted && !header.Grade }" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Grade && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('rate')) }}
                                    </small>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Range" class="col-4">{{ $t('range') }}<span style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <InputNumber name="Range" v-model="header.Range" required="true" type="text"
                                        :placeholder="$t('0')" v-if="header"
                                        :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT"
                                        mode="decimal" :min-fraction-digits="0" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small v-if="header && !header.Range && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('range')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Use_Sub_Range" class="col-4">{{ $t('use_sub_range') }}</label>
                                <div class="col-8 md-8">
                                    <Checkbox v-model="header.Use_Sub_Range" v-if="header" 
                                        :binary="true" 
                                        :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT" />
                                    <Skeleton v-else-if="!header" />
                                </div>
                            </div>
                            <div class="field grid" v-if="header && header.Use_Sub_Range">
                                <label for="Sub_Range_Plus_From" class="col-4">{{ $t('sub_rate') }} (+)</label>
                                <div class="col-8 md-8">
                                    <div class="grid">
                                        <div class="col-6">
                                            <div class="p-inputgroup">
                                                <span class="p-inputgroup-addon">from</span>
                                                <InputNumber name="Sub_Range_Plus_From" v-model="header.Sub_Range_Plus_From" 
                                                    type="text" :placeholder="$t('0')" v-if="header"
                                                    :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT"
                                                    mode="decimal" :min-fraction-digits="0" :min="header.Range || 0" />
                                            </div>
                                        </div>
                                        <div class="col-6">
                                            <div class="p-inputgroup">
                                                <span class="p-inputgroup-addon">to</span>
                                                <InputNumber name="Sub_Range_Plus_To" v-model="header.Sub_Range_Plus_To" 
                                                    type="text" :placeholder="$t('0')" v-if="header"
                                                    :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT"
                                                    mode="decimal" :min-fraction-digits="0" :min="header.Sub_Range_Plus_From || header.Range || 0" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="field grid" v-if="header && header.Use_Sub_Range">
                                <label for="Sub_Range_Minus_From" class="col-4">{{ $t('sub_rate') }} (-)</label>
                                <div class="col-8 md-8">
                                    <div class="grid">
                                        <div class="col-6">
                                            <div class="p-inputgroup">
                                                <span class="p-inputgroup-addon">from</span>
                                                <InputNumber name="Sub_Range_Minus_From" v-model="header.Sub_Range_Minus_From" 
                                                    type="text" :placeholder="$t('0')" v-if="header"
                                                    :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT"
                                                    mode="decimal" :min-fraction-digits="0" :min="header.Range || 0" />
                                            </div>
                                        </div>
                                        <div class="col-6">
                                            <div class="p-inputgroup">
                                                <span class="p-inputgroup-addon">to</span>
                                                <InputNumber name="Sub_Range_Minus_To" v-model="header.Sub_Range_Minus_To" 
                                                    type="text" :placeholder="$t('0')" v-if="header"
                                                    :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT"
                                                    mode="decimal" :min-fraction-digits="0" 
                                                    :min="header.Sub_Range_Minus_From || header.Range || 0" 
                                                    :max="header.Sub_Range_Plus_From || undefined" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Standard" class="col-4">{{ $t('standard') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <InputNumber name="Standard" v-model="header.Standard" required="true" type="text"
                                        :placeholder="$t('0')" v-if="header"
                                        :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT"
                                        mode="decimal" :min-fraction-digits="0" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small v-if="header && !header.Standard && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('standard')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Tolerance_Up" class="col-4">{{ $t('tolerance_up') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <InputNumber name="Tolerance_Up" v-model="header.Tolerance_Up" required="true"
                                        type="text" :placeholder="$t('0')" v-if="header"
                                        :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT"
                                        mode="decimal" :min-fraction-digits="0" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small
                                        v-if="header && !header.Tolerance_Up && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('tolerance_up')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Tolerance_Down" class="col-4">{{ $t('tolerance_down') }}<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <InputNumber name="Tolerance_Down" v-model="header.Tolerance_Down" required="true"
                                        type="text" :placeholder="$t('0')" v-if="header"
                                        :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT"
                                        mode="decimal" :min-fraction-digits="0" />
                                    <Skeleton v-else-if="!header" />
                                    <!-- <small
                                        v-if="header && !header.Tolerance_Down && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('tolerance_down')) }}
                                    </small> -->
                                </div>
                            </div>
                            <div class="field grid">
                                <label for="Summary" class="col-4">{{ $t('summary_rate') }} (Rate)<span
                                        style="color: red;">*</span></label>
                                <div class="col-8 md-8">
                                    <InputText name="Summary" v-model="header.Summary" required="true" type="text"
                                        :placeholder="$t('summary_rate')" v-if="header"
                                        :class="{ 'p-invalid': submitted && !header.Summary }"
                                        :disabled="mode != this.$FORM_MODE_CREATE && mode != this.$FORM_MODE_EDIT" />
                                    <Skeleton v-else-if="!header" />
                                    <small v-if="header && !header.Summary && submitted && mode != this.$FORM_MODE_DELETE"
                                        class="p-error">{{ $t('is_required').replace('Value', $t('summary')) }}
                                    </small>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>
        </Card>
    </form>
</template>


<script>
export default {
    name: "Editor",
    data() {
        return {
            indexName: "CriteriaIndex",
            url: this.$API_URL + "criteria/",
            header: null,
            detail: null,
            mode: "",
            submitted: false,
            id: 0,
            formEditable: true,
        }
    },
    watch: {
        'header.Use_Sub_Range'(newValue) {
            if (!newValue && this.header) {
                this.header.Sub_Range_Plus_From = 0;
                this.header.Sub_Range_Plus_To = 0;
                this.header.Sub_Range_Minus_From = 0;
                this.header.Sub_Range_Minus_To = 0;
            }
        }
    },    
    methods: {
        getData() {
            var self = this;
            this.$axios.get(this.url + this.id + "/" + this.mode)
                .then((response) => {
                    if (response.data.success) {
                        self.header = response.data.data.header;
                        self.header.mode = self.mode;

                        if (self.header.Use_Sub_Range === undefined || self.header.Use_Sub_Range === null) {
                            self.header.Use_Sub_Range = false;
                        }                        
                    }
                })
                .catch(function (error) {
                    alert(error);
                });
        },
        saveData() {
            this.$emit('block-ui');
            var self = this;
            this.$axios.post(this.url, this.header)
                .then((response) => {
                    if (response.data.success) {
                        if (this.mode == this.$FORM_MODE_DELETE) {
                            self.$router.push({ name: self.indexName, params: { showToast: true, severity: 'success', summary: 'Data Deleted', detail: "Criteria " + self.header.Code + " has been deleted successfully", life: 3000 } });
                        } else {
                            self.$router.push({ name: self.indexName, params: { showToast: true, severity: 'success', summary: 'Data Saved', detail: "Criteria " + self.header.Code + " has been saved successfully", life: 3000 } });
                        }

                    }
                    else {
                        self.showError('Error Saving Data', response.data.message);
                    }
                })
                .catch(function (error) {
                    self.showError('Error Saving Data', error);
                });
        },
        showError(summary, message) {
            this.$emit('unblock-ui');
            this.$toast.add({ severity: 'error', summary: summary, detail: message, life: 3000 });
        },
        backToIndex() {
            this.$router.push({ path: "/Criteria/Index" });
        },
        toggle(event) {
            this.$refs.op.toggle(event);
        },
        confirm(event) {
            this.submitted = true;
            if (!this.header.Period_Year || !this.header.Grade || !this.header.Summary) {
                return;
            } else {
                this.$confirm.require({
                    target: event.currentTarget,
                    message: 'Are you sure?',
                    icon: 'pi pi-exclamation-triangle',
                    accept: () => {
                        this.saveData();
                    },
                    reject: () => {

                    }
                });
            }

        },
        hideRow(data) {
            return data.mode == this.$FORM_MODE_DELETE ? 'row-hidden' : null;
        },
        addDetail() {
            var newDetail = this.detail;
            newDetail.mode = this.$FORM_MODE_CREATE;
            var det = { ...newDetail };

            this.header.Details.push(det);
        },
        deleteDetail(event) {
            if (event) {
                var detail = this.header.Details[event.index];

                if (detail.mode == this.$FORM_MODE_CREATE) this.header.Details.splice(this.header.Details.indexOf(detail), 1);
                else detail.mode = this.$FORM_MODE_DELETE;
            }
        },
        onCellEditComplete(event) {
            this.editDetail(event.index);
        },
        editDetail(index) {
            if (this.header && this.header.Details) {
                let mode = this.header.Details[index].mode;

                if (mode == this.$FORM_MODE_UNCHANGED) {
                    this.header.Details[index].mode = this.$FORM_MODE_EDIT;
                }
            }
        },
    },
    created() {
        this.mode = this.$route.query.mode;
        this.id = this.$route.query.id ?? 0;

        this.getData();
    },
    computed: {
        years() {
            let years = [];

            for (var i = new Date().getFullYear(); i >= 2023; i--) {
                years.push({ value: i.toString() })
            }
            return years;
        },
        rates() {
            let rates = [];
            rates.push({ value: 'A' })
            rates.push({ value: 'B' })
            rates.push({ value: 'C' })
            rates.push({ value: 'D' })
            rates.push({ value: 'E' })
            return rates;
        }
    }
}
</script>